import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { pathToEvaluatorEdit } from '@/navigation/navigate';
import { engineService } from '@/services/engine-service';
import { pollForResource } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { engineQueryKeys, evaluatorMutationKeys } from './query-keys';

/**
 * Hook to create an evaluator.
 * Polls until the read model reflects the new evaluator, then navigates to its edit page.
 */
export function useCreateEvaluator(tenantId?: UUID, tenantSlug?: string) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationKey: evaluatorMutationKeys.createEvaluator(tenantId),
    mutationFn: async (payload: {
      name: string;
      description?: string;
      enabled?: boolean;
      priority?: number;
      trigger: { eventType: string; eventSpace: string };
      output: { eventType: string };
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');

      const ruleId = await engineService.createEvaluator(tenantId, payload);

      await pollForResource(() =>
        engineService.findEvaluator(tenantId, ruleId)
      );

      return ruleId;
    },
    onSuccess: (ruleId: UUID) => {
      qc.invalidateQueries({
        queryKey: engineQueryKeys.evaluatorListByTenant(tenantId),
        refetchType: 'all',
      });

      if (tenantSlug) {
        navigate(pathToEvaluatorEdit(tenantSlug, ruleId));
      }
    },
  });
}
